"use client";

import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod/v4";
import { cn } from "@drpcrd/utils/shared";
import { Form } from "../form";
import { InputTextArea } from "../input-text-area";
import { CreatorPageSelectableListRenderer } from "../creator-page/creator-page-selectable-list-renderer";
import {
  OUTREACH_DECLINE_REASONS,
  getDeclineReasonTitles,
} from "./outreach-decline-reasons";
import type { OutreachResponseFooter } from "./types";

const schema = z.object({
  reasons: z
    .array(z.string())
    .min(1, { error: "Please select at least one reason" }),
  comment: z.string().optional(),
});

type FormData = z.infer<typeof schema>;

type Props = {
  /** Receives the chosen reasons as their display titles, plus the trimmed
   * comment when one was left. */
  onSubmit: (reasons: string[], comment?: string) => void;
  pending: boolean;
  footer: OutreachResponseFooter;
  /** Applied to the fields wrapper — how the consuming screen pads/sizes
   * the form; the footer sits outside it. */
  className?: string;
};

/**
 * "No thanks" response to a brand's outreach — at least one reason, plus an
 * optional comment. Shared by the creator app and the talent agency app;
 * the caller supplies the submit control through `footer`.
 */
export const OutreachNotInterestedForm = ({
  onSubmit,
  pending,
  footer,
  className,
}: Props) => {
  const [selectedReasons, setSelectedReasons] = useState<string[]>([]);
  const form = useForm<FormData>({
    resolver: zodResolver(schema),
    mode: "onSubmit",
    defaultValues: { reasons: [], comment: "" },
  });

  const onReasonSelect = (reason: string) => {
    const nextReasons = selectedReasons.includes(reason)
      ? selectedReasons.filter((id) => id !== reason)
      : [...selectedReasons, reason];
    setSelectedReasons(nextReasons);
    form.setValue("reasons", nextReasons);
  };

  const submit = form.handleSubmit((data) =>
    onSubmit(getDeclineReasonTitles(data.reasons), data.comment?.trim()),
  );

  return (
    <>
      <div className={cn("flex w-full grow flex-col", className)}>
        <form onSubmit={submit} className="flex w-full grow flex-col gap-3">
          <Form {...form}>
            <CreatorPageSelectableListRenderer
              items={[...OUTREACH_DECLINE_REASONS]}
              selectedItemIds={selectedReasons}
              onItemSelectAction={onReasonSelect}
              disabled={pending}
            />

            <InputTextArea
              control={form.control}
              name="comment"
              label="Leave a comment"
              placeholder="Type here"
            />
          </Form>
        </form>
      </div>

      {footer({
        submit,
        disabled: selectedReasons.length === 0 || pending,
        pending,
      })}
    </>
  );
};
